import { IntegrationQuestionnairePayload } from "src/features/site-settings/types/payloads/integration-questionnaire";
import { SiteIntegrationQuestionnaireState } from "src/features/site/types/enum/site-integration-questionnaire-state";
import { IntegrationQuestionnaire } from "src/features/site/types/responses/integration-questionnaire";
import { IntegrationQuestionnaireQuestion } from "src/features/site/types/responses/integration-questionnaire-question";
import { OrganizationOnboardingPayload } from "src/types/payloads/onboarding-payload";
import { ApproveOrganizationRemoval } from "src/types/responses/approve-organization-removal";
import { OrganizationContact } from "src/types/responses/organization-contact";
import { OrganizationInfo } from "src/types/responses/organization-info";
import { RemoveOrganization } from "src/types/responses/remove-organization";
import { RemoveOrganizationStatus } from "src/types/responses/remove-organization-status";
import { Address, Site } from "src/types/responses/site";
import orgApiV4 from "../../../../api/clients/org-api-v4";

class OrganizationDetailsApi {
  async fetchOrganization(): Promise<OrganizationInfo> {
    const { data } = await orgApiV4.get(`/organization`);
    return data;
  }

  async fetchOrganizationContacts(): Promise<OrganizationContact[]> {
    const { data } = await orgApiV4.get<OrganizationContact[]>("/organization-contact-details");

    return data;
  }

  async createContact(
    firstName: string,
    lastName: string,
    position: string,
    email: string,
    phoneNumber: string,
  ): Promise<OrganizationContact> {
    const { data } = await orgApiV4.post<OrganizationContact>("/organization-contact-details", {
      firstName,
      lastName,
      position,
      email,
      phoneNumber: phoneNumber?.toString(),
    });

    return data;
  }

  async updateContact(
    contactToken: string,
    firstName: string,
    lastName: string,
    position: string,
    email: string,
    phoneNumber: string,
  ): Promise<OrganizationContact> {
    const { data } = await orgApiV4.patch<OrganizationContact>(
      `/organization-contact-details/${contactToken}`,
      {
        firstName,
        lastName,
        position,
        email,
        phoneNumber,
      },
    );
    return data;
  }

  async removeOrganizationContact(contactToken: string): Promise<void> {
    await orgApiV4.delete(`/organization-contact-details/${contactToken}`);
  }

  async fetchOrganizationSites(): Promise<Site[]> {
    const { data } = await orgApiV4.get<Site[]>("/sites");

    return data;
  }

  async createSite(name: string, address: Address, timezone?: string): Promise<Site> {
    const { data } = await orgApiV4.post<Site>("/sites", {
      name,
      timezone,
      ...address,
    });

    return data;
  }

  async editSite(
    siteToken: string,
    name: string,
    address: Address,
    timezone?: string,
  ): Promise<Site> {
    const { data } = await orgApiV4.patch<Site>(`/sites/${siteToken}`, {
      name,
      timezone,
      ...address,
    });
    return data;
  }

  async uploadIntegrationQuestionnaireImages(
    siteToken: string,
    images: FormData,
  ): Promise<{ url: string }[]> {
    const { data } = await orgApiV4.post<{ url: string }[]>(
      `/sites/${siteToken}/integration-questionnaire/photos`,
      images,
    );

    return data;
  }

  async fetchIntegrationQuestionnaire(siteToken: string): Promise<IntegrationQuestionnaire> {
    const { data } = await orgApiV4.get<IntegrationQuestionnaire>(
      `/sites/${siteToken}/integration-questionnaire`,
    );
    return data;
  }

  async fetchIntegrationQuestionnaireQuestions(): Promise<IntegrationQuestionnaireQuestion[]> {
    const { data } = await orgApiV4.get<IntegrationQuestionnaireQuestion[]>(
      `/sites/integration-questionnaire/questions`,
    );

    return data;
  }

  async sendIntegrationQuestionnaire(
    siteToken: string,
    data: {
      questionnaire: IntegrationQuestionnairePayload[];
      photo1?: string;
      photo2?: string;
      state: SiteIntegrationQuestionnaireState;
    },
  ): Promise<void> {
    await orgApiV4.post<void>(`/sites/${siteToken}/integration-questionnaire`, data);
  }

  async completeRegistration(values: OrganizationOnboardingPayload): Promise<void> {
    await orgApiV4.post(`/organization/complete-registration`, values);
  }

  async removeOrganization(organizationName: string): Promise<RemoveOrganization> {
    const { data } = await orgApiV4.post<RemoveOrganization>(`/organization/remove`, {
      organizationName,
    });

    return data;
  }

  async fetchRemoveOrganizationStatus(): Promise<RemoveOrganizationStatus> {
    const { data } = await orgApiV4.get<RemoveOrganizationStatus>(`/organization/remove/status`);

    return data;
  }

  async approveRemoveOrganization(): Promise<ApproveOrganizationRemoval> {
    const { data } = await orgApiV4.post<ApproveOrganizationRemoval>(
      `/organization/remove/approve`,
    );

    return data;
  }

  async rejectRemoveOrganization(): Promise<void> {
    await orgApiV4.delete<void>(`/organization/remove/reject`);
  }
}

export const organizationDetailsApi = new OrganizationDetailsApi();
